import { useState, useEffect } from 'react';
import { CreditCard, Settings, AlertCircle, CheckCircle, Plus, Trash2, Edit2, X, Save } from 'lucide-react';
import api from '../../services/api';

const PaymentProvidersSettings = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    provider: 'stripe',
    isActive: true,
    isSandbox: true,
    publicKey: '',
    secretKey: '',
    webhookSecret: '',
    webhookEndpoint: '',
    configuration: {}
  });

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    setLoading(true);
    try {
      const response = await api.getPaymentProviders();
      setProviders(response.data || []);
    } catch (err) {
      setError('Failed to load payment providers');
      console.error('Error loading providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (editingProvider) {
        await api.updatePaymentProvider(editingProvider._id, formData);
        setSuccess('Payment provider updated successfully');
      } else {
        await api.createPaymentProvider(formData);
        setSuccess('Payment provider added successfully');
      }
      
      await loadProviders();
      resetForm();
    } catch (err) {
      setError(err.message || 'Failed to save payment provider');
      console.error('Error saving provider:', err);
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      provider: provider.provider,
      isActive: provider.isActive,
      isSandbox: provider.isSandbox,
      publicKey: provider.publicKey || '',
      secretKey: provider.secretKey || '',
      webhookSecret: provider.webhookSecret || '',
      webhookEndpoint: provider.webhookEndpoint || '',
      configuration: provider.configuration || {}
    });
    setShowForm(true);
  };

  const handleDelete = async (providerId) => {
    if (!window.confirm('Are you sure you want to delete this payment provider?')) {
      return;
    }

    try {
      await api.deletePaymentProvider(providerId);
      setSuccess('Payment provider deleted successfully');
      await loadProviders();
    } catch (err) {
      setError('Failed to delete payment provider');
      console.error('Error deleting provider:', err);
    }
  };

  const resetForm = () => {
    setEditingProvider(null);
    setFormData({
      provider: 'stripe',
      isActive: true,
      isSandbox: true,
      publicKey: '',
      secretKey: '',
      webhookSecret: '',
      webhookEndpoint: '',
      configuration: {}
    });
    setShowForm(false);
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'stripe':
        return '💳';
      case 'gocardless':
        return '🏦';
      default:
        return '💰';
    }
  };

  const getProviderBadge = (provider) => {
    if (provider.isActive) {
      return provider.isSandbox ? 
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Sandbox</span> : 
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Live</span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Inactive</span>;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-8 text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h5 className="flex items-center text-lg font-semibold text-gray-800">
              <CreditCard size={20} className="mr-2" />
              Payment Providers
            </h5>
            <p className="text-sm text-gray-600 mt-1">
              Configure payment gateways for online payments
            </p>
          </div>
          {!showForm && (
            <button 
              className="flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
              onClick={() => setShowForm(true)}
            >
              <Plus size={16} className="mr-1" />
              Add Provider
            </button>
          )}
        </div>
      </div>
      
      <div className="p-6">
        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle size={16} className="mr-2" />
              {error}
            </div>
            <button 
              className="text-red-700 hover:text-red-900"
              onClick={() => setError(null)}
            >
              <X size={16} />
            </button>
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md mb-4 flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle size={16} className="mr-2" />
              {success}
            </div>
            <button 
              className="text-green-700 hover:text-green-900"
              onClick={() => setSuccess(null)}
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Provider Form */}
        {showForm && (
          <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h6 className="text-lg font-semibold text-gray-800 mb-4">
              {editingProvider ? 'Edit Payment Provider' : 'Add Payment Provider'}
            </h6>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    disabled={editingProvider}
                  >
                    <option value="stripe">Stripe</option>
                    <option value="gocardless">GoCardless</option>
                  </select>
                </div>
                
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div className="flex items-center mt-2">
                    <input
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <label className="ml-2 text-sm text-gray-700" htmlFor="isActive">
                      Active
                    </label>
                  </div>
                </div>
                
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
                  <div className="flex items-center mt-2">
                    <input
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      type="checkbox"
                      id="isSandbox"
                      checked={formData.isSandbox}
                      onChange={(e) => setFormData({ ...formData, isSandbox: e.target.checked })}
                    />
                    <label className="ml-2 text-sm text-gray-700" htmlFor="isSandbox">
                      Sandbox Mode
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.provider === 'stripe' ? 'Publishable Key' : 'Access Token'}
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={formData.publicKey}
                    onChange={(e) => setFormData({ ...formData, publicKey: e.target.value })}
                    placeholder={formData.provider === 'stripe' ? 'pk_test_...' : 'access_token_...'}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.provider === 'stripe' ? 'Secret Key' : 'Secret Key'}
                  </label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={formData.secretKey}
                    onChange={(e) => setFormData({ ...formData, secretKey: e.target.value })}
                    placeholder={formData.provider === 'stripe' ? 'sk_test_...' : 'secret_key_...'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Webhook Secret</label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={formData.webhookSecret}
                    onChange={(e) => setFormData({ ...formData, webhookSecret: e.target.value })}
                    placeholder="whsec_..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used to verify webhook signatures
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Webhook Endpoint</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={formData.webhookEndpoint}
                    onChange={(e) => setFormData({ ...formData, webhookEndpoint: e.target.value })}
                    placeholder="https://yourdomain.com/api/webhooks/stripe"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your webhook URL for this provider
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button 
                  type="button" 
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Save size={16} className="mr-1" />
                  {editingProvider ? 'Update Provider' : 'Add Provider'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Providers List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {providers.length === 0 && !showForm && (
            <div className="col-span-full text-center py-12">
              <Settings size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No payment providers configured</p>
              <button 
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors mx-auto"
                onClick={() => setShowForm(true)}
              >
                <Plus size={16} className="mr-1" />
                Add Your First Provider
              </button>
            </div>
          )}
          
          {providers.map(provider => (
            <div key={provider._id} className="bg-white border border-gray-200 rounded-lg p-6 h-full">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h6 className="flex items-center text-lg font-semibold text-gray-800 mb-2">
                    <span className="mr-2">{getProviderIcon(provider.provider)}</span>
                    {provider.provider.charAt(0).toUpperCase() + provider.provider.slice(1)}
                  </h6>
                  {getProviderBadge(provider)}
                </div>
                <div className="flex gap-2">
                  <button 
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    onClick={() => handleEdit(provider)}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button 
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    onClick={() => handleDelete(provider._id)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              
              <div className="text-sm space-y-3">
                <div>
                  <div className="font-medium text-gray-700">Public Key:</div>
                  <code className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {provider.publicKey ? `${provider.publicKey.substring(0, 20)}...` : 'Not configured'}
                  </code>
                </div>
                
                {provider.webhookEndpoint && (
                  <div>
                    <div className="font-medium text-gray-700">Webhook:</div>
                    <div className="text-xs text-gray-500 break-all">{provider.webhookEndpoint}</div>
                  </div>
                )}
                
                <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                  Updated: {new Date(provider.updatedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Help Section */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h6 className="flex items-center text-sm font-semibold text-blue-800 mb-2">
            <AlertCircle size={16} className="mr-1" />
            Payment Provider Setup
          </h6>
          <div className="text-sm text-blue-700">
            <ul className="list-disc list-inside space-y-1">
              <li>Always test with sandbox/test credentials first</li>
              <li>Configure webhook endpoints in your payment provider's dashboard</li>
              <li>Keep your secret keys secure and never expose them in client-side code</li>
              <li>Switch to live mode only after thorough testing</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentProvidersSettings;